import { useEffect, useMemo, useRef, useState } from "react";

import { useNavigate } from "react-router-dom";

import dayjs from "dayjs";
import {
  ArrowLeft,
  Download,
  FileText,
  Loader2,
  Paperclip,
  Plus,
  RefreshCw,
  ShoppingCart,
  Trash,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { SearchableSelect } from "~/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  useCostingMovements,
  useCreateCostingMovement,
  useDeleteCostingMovement,
  useUpdateCostingMovement,
} from "~/hooks/costing";
import { useOilTypes, useRawMaterials } from "~/hooks/purchasing";
import { useToast } from "~/hooks/use-toast";
import { costingService } from "~/services/costing";
import type { CostingItemKind, CostingMovement } from "~/types/costing";

import {
  ITEM_KIND_LABELS,
  PdfViewerDialog,
  PeriodRangeSelector,
  fmtCLP,
  fmtQty,
  oilLedgerRowToKg,
  useCanEditCosting,
  useCosteoPeriod,
  useCostingProducts,
  useOilDensities,
} from "./components/CosteoShared";
import { exportPurchasesToExcel } from "./utils/exportPurchases";

interface ManualForm {
  item_kind: CostingItemKind;
  item_id: string;
  quantity: string;
  unit_cost: string;
  document_number: string;
  document_date: string;
  notes: string;
  file: File | null;
}

const emptyForm: ManualForm = {
  item_kind: "raw_material",
  item_id: "",
  quantity: "",
  unit_cost: "",
  document_number: "",
  document_date: dayjs().format("YYYY-MM-DD"),
  notes: "",
  file: null,
};

export function CosteoComprasPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const canEdit = useCanEditCosting();

  const { periods, period, periodRow, setPeriod } = useCosteoPeriod();

  // Month range: `period` (from useCosteoPeriod) is the "Hasta" / working period;
  // `fromP` is the "Desde" month. Both default to the current open period, so the
  // default view is a single month and behaves exactly as before.
  const [fromP, setFromP] = useState("");
  useEffect(() => {
    if (period && !fromP) setFromP(period);
  }, [period, fromP]);

  const effFrom = fromP || period;
  const [lo, hi] = effFrom <= period ? [effFrom, period] : [period, effFrom];
  const isRange = !!lo && !!hi && lo !== hi;

  const periodIdsInRange = useMemo(
    () =>
      lo && hi
        ? periods
            .filter((p) => p.period >= lo && p.period <= hi)
            .map((p) => p.id)
        : [],
    [periods, lo, hi]
  );

  const periodLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of periods) map.set(p.id, p.period);
    return map;
  }, [periods]);

  const { data: purchases = [], isLoading } = useCostingMovements(
    periodIdsInRange,
    {
      movementClass: "purchase",
    }
  );

  const { data: rawMaterials = [] } = useRawMaterials();
  const { data: oilTypes = [] } = useOilTypes();
  const { data: products = [] } = useCostingProducts();
  const { data: densities } = useOilDensities();

  const createMovement = useCreateCostingMovement();
  const updateMovement = useUpdateCostingMovement();
  const deleteMovement = useDeleteCostingMovement();

  const [search, setSearch] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [form, setForm] = useState<ManualForm>(emptyForm);
  const [viewingPdf, setViewingPdf] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<CostingMovement | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [attachTarget, setAttachTarget] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isOpen = periodRow?.status === "open";
  // Editing (sync / manual entry / attach / delete) targets a single open period.
  // A multi-month range has no single working period → read-only + export only.
  const canEditNow = canEdit && !isRange && isOpen;
  const columnCount = 8 + (isRange ? 1 : 0) + (canEditNow ? 1 : 0);

  const itemOptions = useMemo(() => {
    if (form.item_kind === "raw_material") {
      return rawMaterials.map((m) => ({
        value: m.id,
        label: `${m.code} — ${m.name}`,
      }));
    }
    if (form.item_kind === "oil_type") {
      return oilTypes.map((o) => ({
        value: o.id,
        label: `${o.code} — ${o.name}`,
      }));
    }
    return products.map((p) => ({
      value: p.id,
      label: `${p.sku ?? "—"} — ${p.short_name}`,
    }));
  }, [form.item_kind, rawMaterials, oilTypes, products]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return purchases;
    return purchases.filter((m) => {
      const code =
        m.raw_materials?.code ?? m.oil_types?.code ?? m.products?.sku ?? "";
      const name =
        m.raw_materials?.name ??
        m.oil_types?.name ??
        m.products?.short_name ??
        "";
      return (
        code.toLowerCase().includes(q) ||
        name.toLowerCase().includes(q) ||
        (m.document_number ?? "").toLowerCase().includes(q) ||
        (m.notes ?? "").toLowerCase().includes(q)
      );
    });
  }, [purchases, search]);

  // Aceites are liters-native in the engine but counted in kg by finance/Softland;
  // present quantity + unit cost in kg (the $ total is unchanged).
  const displayPurchases = useMemo(
    () =>
      filtered.map((m) =>
        m.item_kind === "oil_type"
          ? oilLedgerRowToKg(m, densities?.[m.oil_type_id ?? ""])
          : m
      ),
    [filtered, densities]
  );

  const totals = useMemo(
    () => filtered.reduce((s, m) => s + Number(m.total_cost_clp), 0),
    [filtered]
  );

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await costingService.syncPurchases(period);
      await costingService.recomputeWac(period);
      toast({
        title: "Recepciones sincronizadas",
        description: `${result.synced} líneas de compra cargadas desde recepciones`,
      });
    } catch (error) {
      toast({
        title: "Error al sincronizar",
        description: error instanceof Error ? error.message : String(error),
        variant: "destructive",
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleCreate = async () => {
    const quantity = Number(form.quantity);
    const unitCost = Number(form.unit_cost);
    if (
      !form.item_id ||
      !quantity ||
      quantity <= 0 ||
      !unitCost ||
      unitCost < 0
    ) {
      toast({
        title: "Error",
        description: "Complete ítem, cantidad y costo unitario",
        variant: "destructive",
      });
      return;
    }
    if (!periodRow) return;

    try {
      let attachmentPath: string | null = null;
      if (form.file) {
        attachmentPath = await costingService.uploadDocument(form.file, period);
      }

      await createMovement.mutateAsync({
        period_id: periodRow.id,
        movement_date: form.document_date,
        item_kind: form.item_kind,
        raw_material_id:
          form.item_kind === "raw_material" ? form.item_id : null,
        oil_type_id: form.item_kind === "oil_type" ? form.item_id : null,
        product_id: form.item_kind === "finished_good" ? form.item_id : null,
        movement_class: "purchase",
        quantity,
        unit_cost_clp: unitCost,
        document_type: "dte",
        document_number: form.document_number || null,
        document_date: form.document_date,
        attachment_url: attachmentPath,
        notes: form.notes || null,
      });

      toast({ title: "Compra registrada", description: form.document_number });
      setIsDialogOpen(false);
      setForm(emptyForm);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : String(error),
        variant: "destructive",
      });
    }
  };

  const handleAttach = async (file: File) => {
    if (!attachTarget) return;
    try {
      const path = await costingService.uploadDocument(file, period);
      await updateMovement.mutateAsync({
        id: attachTarget,
        input: { attachment_url: path },
      });
      toast({ title: "Documento adjuntado", description: file.name });
    } catch (error) {
      toast({
        title: "Error al adjuntar",
        description: error instanceof Error ? error.message : String(error),
        variant: "destructive",
      });
    } finally {
      setAttachTarget(null);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMovement.mutateAsync(deleting.id);
      toast({ title: "Compra eliminada" });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : String(error),
        variant: "destructive",
      });
    } finally {
      setDeleting(null);
    }
  };

  const handleExport = () => {
    exportPurchasesToExcel(displayPurchases, periodLabel, { from: lo, to: hi });
  };

  return (
    <PageContainer>
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/costeo")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <ShoppingCart className="h-6 w-6" />
            Compras / Recepciones
          </h1>
          <p className="text-muted-foreground">
            El informe de recepción es la fuente de verdad — el PDF de respaldo
            se puede ver aquí mismo
          </p>
        </div>
        <PeriodRangeSelector
          periods={periods}
          from={effFrom}
          to={period}
          onFromChange={setFromP}
          onToChange={setPeriod}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por código, ítem, documento o proveedor…"
          className="h-9 max-w-sm"
        />
        <div className="flex-1" />
        <Button
          variant="outline"
          className="h-9"
          onClick={handleExport}
          disabled={displayPurchases.length === 0}
        >
          <Download className="mr-2 h-4 w-4" />
          Exportar Excel
        </Button>
        {canEditNow && (
          <>
            <Button
              variant="outline"
              className="h-9"
              onClick={handleSync}
              disabled={syncing}
            >
              {syncing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Sincronizar Recepciones
            </Button>
            <Button className="h-9" onClick={() => setIsDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Compra Manual (DTE)
            </Button>
          </>
        )}
      </div>

      <div className="overflow-hidden rounded-lg border bg-white shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              {isRange && <TableHead className="w-24">Período</TableHead>}
              <TableHead className="w-28">Fecha</TableHead>
              <TableHead>Ítem</TableHead>
              <TableHead className="w-36">Documento</TableHead>
              <TableHead className="w-28">Origen</TableHead>
              <TableHead className="text-right">Cantidad</TableHead>
              <TableHead className="text-right">Costo Unit.</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="w-28 text-right">PDF</TableHead>
              {canEditNow && <TableHead className="w-12" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={columnCount}>
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columnCount}
                  className="py-8 text-center text-muted-foreground"
                >
                  Sin compras registradas en{" "}
                  {isRange ? `${lo} – ${hi}` : period || "este período"}
                </TableCell>
              </TableRow>
            ) : (
              displayPurchases.map((m, index) => {
                const code =
                  m.raw_materials?.code ?? m.oil_types?.code ?? m.products?.sku;
                const name =
                  m.raw_materials?.name ??
                  m.oil_types?.name ??
                  m.products?.short_name;
                const isManual = m.source_type === "manual";
                return (
                  <TableRow
                    key={m.id}
                    className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                  >
                    {isRange && (
                      <TableCell className="text-muted-foreground">
                        {periodLabel.get(m.period_id)}
                      </TableCell>
                    )}
                    <TableCell>
                      {dayjs(m.movement_date).format("DD/MM/YYYY")}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{name}</div>
                      <div className="text-xs text-muted-foreground">
                        {code} · {ITEM_KIND_LABELS[m.item_kind]}
                        {m.notes ? ` · ${m.notes}` : ""}
                      </div>
                    </TableCell>
                    <TableCell>{m.document_number ?? "—"}</TableCell>
                    <TableCell>
                      <Badge variant={isManual ? "secondary" : "outline"}>
                        {isManual ? "Manual" : "Recepción"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {fmtQty(m.quantity)}
                    </TableCell>
                    <TableCell className="text-right">
                      {fmtCLP(m.unit_cost_clp)}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {fmtCLP(m.total_cost_clp)}
                    </TableCell>
                    <TableCell className="text-right">
                      {m.attachment_url ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setViewingPdf(m.attachment_url)}
                        >
                          <FileText className="mr-1 h-4 w-4" />
                          Ver
                        </Button>
                      ) : canEditNow ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setAttachTarget(m.id);
                            fileInputRef.current?.click();
                          }}
                        >
                          <Paperclip className="mr-1 h-4 w-4" />
                          Adjuntar
                        </Button>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    {canEditNow && (
                      <TableCell>
                        {isManual && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleting(m)}
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        {filtered.length > 0 && (
          <div className="flex justify-end border-t bg-muted/30 px-4 py-2 text-sm">
            <span className="font-medium">
              Total compras: {fmtCLP(totals)} ({filtered.length} líneas)
            </span>
          </div>
        )}
      </div>

      {/* Hidden file input for per-row attachment */}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleAttach(file);
          e.target.value = "";
        }}
      />

      <PdfViewerDialog path={viewingPdf} onClose={() => setViewingPdf(null)} />

      {/* Manual DTE purchase */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Compra Manual (DTE)</DialogTitle>
            <DialogDescription>
              Para compras recibidas sin orden de compra. Registre según el
              documento de recepción y adjunte el respaldo.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo de ítem</Label>
                <Select
                  value={form.item_kind}
                  onValueChange={(v) =>
                    setForm((prev) => ({
                      ...prev,
                      item_kind: v as CostingItemKind,
                      item_id: "",
                    }))
                  }
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="raw_material">Materia Prima</SelectItem>
                    <SelectItem value="oil_type">Aceite</SelectItem>
                    <SelectItem value="finished_good">
                      Producto Terminado
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Fecha recepción</Label>
                <Input
                  type="date"
                  className="h-9"
                  value={form.document_date}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      document_date: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Ítem *</Label>
              <SearchableSelect
                value={form.item_id}
                onValueChange={(v) =>
                  setForm((prev) => ({ ...prev, item_id: v }))
                }
                options={itemOptions}
                placeholder="Buscar ítem…"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cantidad *</Label>
                <Input
                  type="number"
                  min="0"
                  step="any"
                  className="h-9"
                  value={form.quantity}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, quantity: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Costo unitario (CLP) *</Label>
                <Input
                  type="number"
                  min="0"
                  step="any"
                  className="h-9"
                  value={form.unit_cost}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, unit_cost: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>N° Documento (DTE)</Label>
                <Input
                  className="h-9"
                  value={form.document_number}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      document_number: e.target.value,
                    }))
                  }
                  placeholder="F-12345"
                />
              </div>
              <div className="space-y-2">
                <Label>Proveedor / notas</Label>
                <Input
                  className="h-9"
                  value={form.notes}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, notes: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Documento de respaldo (PDF)</Label>
              <Input
                type="file"
                accept="application/pdf,image/*"
                className="h-9"
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    file: e.target.files?.[0] ?? null,
                  }))
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={createMovement.isPending}>
              {createMovement.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Registrar Compra
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar compra manual</DialogTitle>
            <DialogDescription>
              ¿Eliminar la compra{" "}
              <span className="font-medium">
                {deleting?.document_number ?? ""}
              </span>
              ? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMovement.isPending}
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
